import { useEffect, useRef, useState } from 'react';

import { CfFlowResponse, CfFlowStatus, useGetCfFlowQuery } from './api/cloudflareFlowApi';

/** Where the interval starts, and where it stops growing. */
export const POLL_START_MS = 5_000;
export const POLL_MAX_MS = 60_000;

export const FAST_POLL_MS = 1_000;

/**
 * How long a running setup step is read every second. A flow stuck in one of
 * them goes back to the normal pace rather than reading every second for as
 * long as the tab stays open.
 */
export const FAST_READS_LIMIT_MS = 2 * 60_000;

const FAST_READ_STATUSES: readonly (CfFlowStatus | undefined)[] = ['preflight_running', 'deploying'];

/**
 * How fast the interval grows. A customer watching a 40-second deployment gets
 * a responsive screen; a browser tab left on the propagation screen for two
 * days settles at one request a minute rather than 17,000 of them.
 */
const BACKOFF_FACTOR = 1.5;

const nextInterval = (current: number): number =>
  Math.min(Math.round(current * BACKOFF_FACTOR), POLL_MAX_MS);

export type CfFlowState = {
  flow: CfFlowResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

/**
 * The one read behind every watched screen.
 *
 * Two behaviours matter more than the polling itself:
 *
 *  - **It stops at a terminal state.** `polls` comes from the server, so a
 *    success screen left open overnight makes no requests at all.
 *  - **The interval grows.** The propagation wait can last 48 hours, and a
 *    fixed five-second poll across that is tens of thousands of requests for a
 *    state that changes once.
 */
export const useCfFlow = (
  domain: string | null,
  { checking = false }: { checking?: boolean } = {},
): CfFlowState => {
  const [interval, setIntervalMs] = useState(POLL_START_MS);
  const [reads, setReads] = useState(0);
  const lastStatus = useRef<string | null>(null);
  const lastStatusSeenAt = useRef(0);

  const { data, isLoading, isError, refetch } = useGetCfFlowQuery(
    { domain: domain ?? '' },
    { skip: !domain },
  );

  const polls = data?.polls ?? false;

  useEffect(() => {
    if (!polls) return undefined;

    // Reset whenever the flow actually moves: the customer is watching progress
    // again, and a slow interval carried over from the previous wait would make
    // the next step look stuck.
    if (data?.status !== lastStatus.current) {
      lastStatus.current = data?.status ?? null;
      lastStatusSeenAt.current = Date.now();
      setIntervalMs(POLL_START_MS);
    }

    const setupStepRunning =
      FAST_READ_STATUSES.includes(data?.status) && Date.now() - lastStatusSeenAt.current < FAST_READS_LIMIT_MS;
    const fast = checking || setupStepRunning;

    const timer = setTimeout(
      () => {
        refetch();
        setReads((count) => count + 1);
        if (!fast) setIntervalMs(nextInterval(interval));
      },
      fast ? FAST_POLL_MS : interval,
    );

    return () => clearTimeout(timer);
  }, [polls, checking, interval, reads, data?.status, refetch]);

  return { flow: data, isLoading, isError, refetch };
};
